/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import "./KatexEquationAlterer.css"

import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import * as React from "react"
import { useCallback, useState } from "react"
import { ErrorBoundary } from "react-error-boundary"

import Button from "./Button"
import KatexRenderer from "./KatexRenderer"

export default function KatexEquationAlterer({
  onConfirm,
  initialEquation = ""
}) {
  const [editor] = useLexicalComposerContext()
  const [equation, setEquation] = useState(initialEquation)
  const [inline, setInline] = useState(true)
  const [cursorPosition, setCursorPosition] = useState(0);
  const [value, setValue] = useState('');

  const textareaRef = React.useRef(null);

  const onClick = useCallback(() => {
    onConfirm(equation, inline)
  }, [onConfirm, equation, inline])

  const onCheckboxChange = useCallback(() => {
    setInline(!inline)
  }, [setInline, inline])

  const handleCursorChange = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      setCursorPosition(textarea.selectionStart);
    }
  };

  const addToTextarea = (text) => {
    if(cursorPosition !== null && textareaRef.current) {
      const textarea = textareaRef.current;
      const currentText = textarea.value;
      const newText = currentText.slice(0, cursorPosition) + text + currentText.slice(cursorPosition);
      textarea.value = newText;
      setEquation(newText); // Update the equation state
      setCursorPosition(cursorPosition + text.length); // Move cursor after the inserted text
    }
  }

  const handleChange = (e) => {
    const newValue = e.target.value;
    // Allow only numeric input
    if (/^\d*$/.test(newValue)) {
      setValue(newValue);
    }
  };

  function removeNthLeftRightParenByOpenOrder(src, n = 1) {
  const LEFT = "\\left(";
  const RIGHT = "\\right)";
  const re = /(\\left\()|(\\right\))/g;

  const stack = [];            // stack of open indices
  const pairs = [];            // { open, close }
  let match;

  while ((match = re.exec(src)) !== null) {
    if (match[1]) {
      stack.push(match.index);
    } else {
      if (stack.length === 0) continue;
      const open = stack.pop();
      pairs.push({ open, close: match.index });
    }
  }

  // Sort by opening position (left-to-right)
  pairs.sort((a, b) => a.open - b.open);

  const target = pairs[n - 1];
  if (!target) return src;

  const { open, close } = target;
  const before = src.slice(0, open);
  const inside = src.slice(open + LEFT.length, close);
  const after  = src.slice(close + RIGHT.length);
  return before + inside + after;
}


  function removeNthLeftRightParen(src, n = 1) {
  const leftTok = "\\left(";
  const rightTok = "\\right)";

  // Tokenize every \left( and \right)
  const re = /(\\left\()|(\\right\))/g;

  let match;
  let depth = 0;
  let pairIndex = 0;
  let openPos = -1;
  let closePos = -1;

  while ((match = re.exec(src)) !== null) {
    if (match[1]) { // Found \left(
      depth++;
      if (depth === 1) { // New top-level
        pairIndex++;
        if (pairIndex === n) openPos = match.index;
      }
    } else if (match[2]) { // Found \right)
      if (depth === 1 && pairIndex === n) {
        closePos = match.index;
        break;
      }
      depth--;
    }
  }

  // If n-th pair not found
  if (openPos === -1 || closePos === -1) return src;

  // Remove the n-th pair
  const before = src.slice(0, openPos);
  const inside = src.slice(openPos + leftTok.length, closePos);
  const after = src.slice(closePos + rightTok.length);

  return before + inside + after;
}

  const onDelete = () => {
    if(value === '') return;
    if(isNaN(value)) return;
    const numToDelete = parseInt(value, 10);
    if(numToDelete <= 0) return;
    // if(numToDelete > 10) return; // Limit to prevent excessive deletions
    // const newEquation = removeNthLeftRightParen(equation, numToDelete);
    const newEquation = removeNthLeftRightParenByOpenOrder(equation, numToDelete);
    
    setEquation(newEquation);
  }


  return (
    <>
      <div className="KatexEquationAlterer_defaultRow">
        Inline
        <input type="checkbox" checked={inline} onChange={onCheckboxChange} />
      </div>
      {/* mahesh */}
      <div style={{ display: "flex", flexDirection: "row", gap: "10px" }}>
        <button
          type="button"
          className="toolbar-item"
          onClick={() => {
            addToTextarea("\\alpha");
          }}
        >
          <i className="format alpha-svgrepo-com" />
        </button>

        <button
          type="button"
          className="toolbar-item"
          onClick={() => {
            addToTextarea("\\beta");
          }}
        >
          <i className="format beta-svgrepo-com" />
        </button>

        <button
          type="button"
          className="toolbar-item"
          onClick={() => {
            addToTextarea("\\gamma");
          }}
        >
          <i className="format gamma-svgrepo-com" />
        </button>

        <button
          type="button"
          className="toolbar-item"
          onClick={() => {
            addToTextarea("\\Omega");
          }}
        >
          <i className="format omega-svgrepo-com" />
        </button>

        <button
          type="button"
          className="toolbar-item"
          onClick={() => {
            addToTextarea("\\pi");
          }}
        >
          <i className="format pi-svgrepo-com" />
        </button>
        <button
          type="button"
          className="toolbar-item"
          onClick={() => {
            addToTextarea("\\mu");
          }}
        >
          <span>&micro;</span>
        </button>
        <button
          type="button"
          className="toolbar-item"
          onClick={() => {
            addToTextarea("\\rho");
          }}
        >
          <span>&rho;</span>
        </button>
        <button
          type="button"
          className="toolbar-item"
          onClick={() => {
            addToTextarea("\\omega");
          }}
        >
          <span>&omega;</span>
        </button>
        <button
          type="button"
          className="toolbar-item"
          onClick={() => {
            addToTextarea("\\theta");
          }}
        >
          <span>&theta;</span>
        </button>
        <button
          type="button"
          className="toolbar-item"
          onClick={() => {
            addToTextarea("\\Delta");
          }}
        >
          <span>&Delta;</span>
        </button>
        <button
          type="button"
          className="toolbar-item"
          onClick={() => {
            addToTextarea("\\Phi");
          }}
        >
          <span>&Phi;</span>
        </button>
        <button
          type="button"
          className="toolbar-item"
          onClick={() => {
            addToTextarea("\\pm");
          }}
        >
          <span>&plusmn;</span>
        </button>
        <button
          type="button"
          className="toolbar-item"
          onClick={() => {
            addToTextarea("\\degree C");
          }}
        >
          <span>&deg;</span>
        </button>
      </div>
      <div style={{ display: "flex", flexDirection: "row", gap: "10px" }}>
        <button
          type="button"
          className="toolbar-item"
          onClick={() => {
            addToTextarea("\\sqrt{}");
          }}
        >
          <i className="format square-root" />
        </button>

        <button
          type="button"
          className="toolbar-item"
          onClick={() => {
            addToTextarea("{}^{}");
          }}
        >
          <i className="format superscript-svgrepo-com" />
        </button>
        <button
          type="button"
          className="toolbar-item"
          onClick={() => {
            addToTextarea("\\frac{}{}");
          }}
        >
          <i className="format divide-svgrepo-com" />
        </button>

        <button
          type="button"
          className="toolbar-item"
          onClick={() => {
            addToTextarea("{}_{}");
          }}
        >
          <i className="format subscript-svgrepo-com" />
        </button>

        <button
          type="button"
          className="toolbar-item"
          onClick={() => {
            addToTextarea("\\times");
          }}
        >
          X
        </button>
        <button
          type="button"
          className="toolbar-item"
          onClick={() => {
            addToTextarea("\\,");
          }}
        >
          s
        </button>
        <button
          type="button"
          className="toolbar-item"
          onClick={() => {
            addToTextarea("\\left(");
          }}
        >
          (
        </button>
        <button
          type="button"
          className="toolbar-item"
          onClick={() => {
            addToTextarea("\\right)");
          }}
        >
          )
        </button>
        <button
          type="button"
          className="toolbar-item"
          onClick={() => {
            addToTextarea("\\overline{}");
          }}
        >
          <span>&macr;</span>
        </button>
        <button
          type="button"
          className="toolbar-item"
          onClick={() => {
            addToTextarea("{}^{}_{}\\mathrm{B}^{}_{}");
          }}
        >
          ch
        </button>
      </div>

      <div className="KatexEquationAlterer_defaultRow">Equation </div>
      <div className="KatexEquationAlterer_centerRow">
        {inline ? (
          <input
            onChange={(event) => {
              setEquation(event.target.value);
            }}
            value={equation}
            className="KatexEquationAlterer_textArea"
            onClick={handleCursorChange}
            onKeyUp={handleCursorChange}
            onSelect={handleCursorChange}
            ref={textareaRef}
          />
        ) : (
          <textarea
            onChange={(event) => {
              setEquation(event.target.value);
            }}
            value={equation}
            className="KatexEquationAlterer_textArea"
          />
        )}
      </div>
      <div className="KatexEquationAlterer_defaultRow">Visualization </div>
      <div>
        <div style={{ padding: "1rem", fontFamily: "sans-serif" }}>
          <label>
            Bracket position remove:{" "}
            <input
              type="text"
              value={value}
              onChange={handleChange}
              placeholder="Bracket pair position to remove"
            />
          </label>
          <Button onClick={onDelete}>Delete</Button>
        </div>

        
      </div>
      <div className="KatexEquationAlterer_centerRow">
        <ErrorBoundary onError={(e) => editor._onError(e)} fallback={null}>
          <KatexRenderer
            equation={equation}
            inline={false}
            onDoubleClick={() => null}
          />
        </ErrorBoundary>
      </div>
      <div className="KatexEquationAlterer_dialogActions">
        <Button onClick={onClick}>Confirm</Button>
      </div>
    </>
  );
}
